import { TimerMode } from "@/components/settings/timer-configuration/types"
import { PHASE_TYPES } from "@/constants/Timeline"
import { PhaseType, RoundConfig } from "@/redux/slices/timerConfigurationSlice"

const ALL_PHASES: PhaseType[] = [
  PHASE_TYPES.RANDR,
  PHASE_TYPES.ADVISOR,
  PHASE_TYPES.NEGOTIATION,
  PHASE_TYPES.DECISION,
]

const STANDARD_MULTI_ROUND_PHASES: PhaseType[] = [
  PHASE_TYPES.ADVISOR,
  PHASE_TYPES.NEGOTIATION,
  PHASE_TYPES.DECISION,
]

export function getPhaseTypesForRound(
    mode: TimerMode,
    roundNumber: number
  ): PhaseType[] {
    switch (mode) {
      case TimerMode.SINGLE_ROUND:
        return ALL_PHASES
      case TimerMode.SINGLE_ROUND_ADVISOR_ONLY:
        return [PHASE_TYPES.ADVISOR]
      case TimerMode.MULTI_ROUND_ALL:
      case TimerMode.TEAM_MODE:
      case TimerMode.RESEARCH_MODE:
        if (roundNumber === 1) {
          return ALL_PHASES
        }
        return STANDARD_MULTI_ROUND_PHASES
      case TimerMode.MULTI_ROUND_NEGOTIATION_LOCKED:
        if (roundNumber === 1) {
          return ALL_PHASES
        }
        return [PHASE_TYPES.ADVISOR, PHASE_TYPES.DECISION]
      case TimerMode.MANUAL_MODE:
        if (roundNumber === 1) {
          return ALL_PHASES
        }
        return STANDARD_MULTI_ROUND_PHASES
      default:
        return ALL_PHASES
    }
  }
  
export function getStatusHeadline(
    selectedRound: number,
    activeRound: number,
    gameMode: TimerMode,
    isRandRPhase: boolean,
    isAdvisorPhase: boolean,
    isNegotiationPhase: boolean,
    isDecisionPhase: boolean
  ): string {
    if (selectedRound !== activeRound) {
      return `Previewing Round ${selectedRound}. The simulation is on Round ${activeRound}.`
    }
    const types = getPhaseTypesForRound(gameMode, activeRound)
    let phaseIndex = -1
    if (isRandRPhase) phaseIndex = types.indexOf(PHASE_TYPES.RANDR)
    else if (isAdvisorPhase) phaseIndex = types.indexOf(PHASE_TYPES.ADVISOR)
    else if (isNegotiationPhase) phaseIndex = types.indexOf(PHASE_TYPES.NEGOTIATION)
    else if (isDecisionPhase) phaseIndex = types.indexOf(PHASE_TYPES.DECISION)
    if (phaseIndex >= 0) {
      return `Phase ${phaseIndex + 1} in progress.`
    }
    return 'Following the simulation timeline.'
  }
  
export function getRoundConfigAtIndex(
    roundConfigurations: RoundConfig[] | undefined,
    roundNumber: number,
    fallback: RoundConfig | null
  ): RoundConfig | null {
    const idx = roundNumber - 1
    if (roundConfigurations && roundConfigurations[idx]) {
      return roundConfigurations[idx]
    }
    return fallback
  }
  
export function durationFromRoundConfig(
    phaseType: PhaseType,
    roundConfig: RoundConfig
  ): { minutes: number; isInfinite: boolean } {
    switch (phaseType) {
      case PHASE_TYPES.RANDR:
        return {
          minutes: roundConfig.randrConsultation ?? 0,
          isInfinite: roundConfig.isRandrInfinite ?? false,
        }
      case PHASE_TYPES.ADVISOR:
        return {
          minutes: roundConfig.advisorConsultation ?? 0,
          isInfinite: roundConfig.isAdvisorInfinite ?? false,
        }
      case PHASE_TYPES.NEGOTIATION:
        return {
          minutes: roundConfig.negotiation ?? 0,
          isInfinite: roundConfig.isNegotiationInfinite ?? false,
        }
      case PHASE_TYPES.DECISION:
        return {
          minutes: roundConfig.decisionSubmission ?? 0,
          isInfinite: roundConfig.isDecisionInfinite ?? false,
        }
      default:
        return { minutes: 0, isInfinite: false }
    }
  }
  
export function durationFromPhaseStatus(
    phasesData: {
      phaseType: PhaseType
      duration: number
      isInfinite?: boolean
    }[],
    phaseType: PhaseType
  ): { minutes: number; isInfinite: boolean } | null {
    const row = phasesData.find((p) => p.phaseType === phaseType)
    if (!row) return null
    let minutes = row.duration
    if (minutes > 240 && Number.isFinite(minutes)) {
      minutes = Math.round(minutes / 60)
    }
    return {
      minutes,
      isInfinite: row.isInfinite ?? false,
    }
  }